<script setup lang="ts">
import type {
  EditorToolbarItem,
  EditorSuggestionMenuItem,
  DropdownMenuItem,
} from '@nuxt/ui'
import { mapEditorItems } from '@nuxt/ui/utils/editor'
import type { Editor, JSONContent, Content } from '@tiptap/vue-3'
import { upperFirst } from 'scule'
import { isEqual } from 'ohash'

const externalValue = defineModel<string>()

const {
  json: jsonValue,
  loaded = true,
  readonly = false,
  compact = false,
  live = false,
  placeholder = "Write, type '/' for commands...",
} = defineProps<{
  loaded?: boolean
  readonly?: boolean
  /** Tightens the toolbar gutters for narrow containers such as modals. */
  compact?: boolean
  /**
   * Emit `markdown` while typing instead of on blur only. Needed when the
   * surrounding form reacts to the value before the editor loses focus
   * (character counters, submit buttons disabled on empty input).
   */
  live?: boolean
  placeholder?: string
  json?: JSONContent
}>()
const emit = defineEmits<{
  json: [JSONContent]
  markdown: [string]
}>()

const editorInstance = shallowRef<Editor | null>(null)
const value = shallowRef(externalValue.value)

watchDebounced(
  () => jsonValue,
  (externalJson) => {
    if (!loaded) {
      return
    }
    if (
      !externalJson ||
      isEqual(externalJson, {
        type: 'doc',
        content: [
          {
            type: 'paragraph',
          },
        ],
      })
    ) {
      return
    }
    const editor = editorInstance.value
    const json = editor?.getJSON()

    if (isEqual(json, externalJson)) {
      return
    }
    editor?.commands.setContent(externalJson as Content)
  },
  { debounce: 50 },
)

whenever(
  () => loaded,
  () => {
    value.value = externalValue.value
  },
)

const fixedToolbarItems = [
  [
    {
      kind: 'undo',
      icon: 'i-lucide-undo',
      tooltip: { text: 'Undo' },
    },
    {
      kind: 'redo',
      icon: 'i-lucide-redo',
      tooltip: { text: 'Redo' },
    },
  ],
  [
    {
      icon: 'i-lucide-heading',
      tooltip: { text: 'Headings' },
      content: {
        align: 'start',
      },
      items: [
        {
          kind: 'heading',
          level: 1,
          icon: 'i-lucide-heading-1',
          label: 'Heading 1',
        },
        {
          kind: 'heading',
          level: 2,
          icon: 'i-lucide-heading-2',
          label: 'Heading 2',
        },
        {
          kind: 'heading',
          level: 3,
          icon: 'i-lucide-heading-3',
          label: 'Heading 3',
        },
        {
          kind: 'heading',
          level: 4,
          icon: 'i-lucide-heading-4',
          label: 'Heading 4',
        },
      ],
    },
    {
      icon: 'i-lucide-list',
      tooltip: { text: 'Lists' },
      content: {
        align: 'start',
      },
      items: [
        {
          kind: 'bulletList',
          icon: 'i-lucide-list',
          label: 'Bullet List',
        },
        {
          kind: 'orderedList',
          icon: 'i-lucide-list-ordered',
          label: 'Ordered List',
        },
      ],
    },
    {
      kind: 'blockquote',
      icon: 'i-lucide-text-quote',
      tooltip: { text: 'Blockquote' },
    },
    {
      kind: 'codeBlock',
      icon: 'i-lucide-square-code',
      tooltip: { text: 'Code Block' },
    },
  ],
  [
    {
      kind: 'mark',
      mark: 'bold',
      icon: 'i-lucide-bold',
      tooltip: { text: 'Bold' },
    },
    {
      kind: 'mark',
      mark: 'italic',
      icon: 'i-lucide-italic',
      tooltip: { text: 'Italic' },
    },
    {
      kind: 'mark',
      mark: 'underline',
      icon: 'i-lucide-underline',
      tooltip: { text: 'Underline' },
    },
    {
      kind: 'mark',
      mark: 'strike',
      icon: 'i-lucide-strikethrough',
      tooltip: { text: 'Strikethrough' },
    },
    {
      kind: 'mark',
      mark: 'code',
      icon: 'i-lucide-code',
      tooltip: { text: 'Code' },
    },
  ],
  [
    {
      slot: 'link' as const,
      icon: 'i-ph-link',
    },
  ],
] satisfies EditorToolbarItem[][]

const bubbleToolbarItems = computed(
  () =>
    [
      [
        {
          label: 'Turn into',
          trailingIcon: 'i-lucide-chevron-down',
          activeColor: 'neutral',
          activeVariant: 'ghost',
          tooltip: { text: 'Turn into' },
          content: {
            align: 'start',
          },
          ui: {
            label: 'text-xs',
          },
          items: [
            {
              type: 'label',
              label: 'Turn into',
            },
            {
              kind: 'paragraph',
              label: 'Paragraph',
              icon: 'i-lucide-type',
            },
            {
              kind: 'heading',
              level: 1,
              icon: 'i-lucide-heading-1',
              label: 'Heading 1',
            },
            {
              kind: 'heading',
              level: 2,
              icon: 'i-lucide-heading-2',
              label: 'Heading 2',
            },
            {
              kind: 'heading',
              level: 3,
              icon: 'i-lucide-heading-3',
              label: 'Heading 3',
            },
            {
              kind: 'heading',
              level: 4,
              icon: 'i-lucide-heading-4',
              label: 'Heading 4',
            },
            {
              kind: 'bulletList',
              icon: 'i-lucide-list',
              label: 'Bullet List',
            },
            {
              kind: 'orderedList',
              icon: 'i-lucide-list-ordered',
              label: 'Ordered List',
            },
            {
              kind: 'blockquote',
              icon: 'i-lucide-text-quote',
              label: 'Blockquote',
            },
            {
              kind: 'codeBlock',
              icon: 'i-lucide-square-code',
              label: 'Code Block',
            },
          ],
        },
      ],
      [
        {
          kind: 'mark',
          mark: 'bold',
          icon: 'i-lucide-bold',
          tooltip: { text: 'Bold' },
        },
        {
          kind: 'mark',
          mark: 'italic',
          icon: 'i-lucide-italic',
          tooltip: { text: 'Italic' },
        },
        {
          kind: 'mark',
          mark: 'underline',
          icon: 'i-lucide-underline',
          tooltip: { text: 'Underline' },
        },
        {
          kind: 'mark',
          mark: 'strike',
          icon: 'i-lucide-strikethrough',
          tooltip: { text: 'Strikethrough' },
        },
        {
          kind: 'mark',
          mark: 'code',
          icon: 'i-lucide-code',
          tooltip: { text: 'Code' },
        },
      ],
      [
        {
          slot: 'link' as const,
          icon: 'i-ph-link',
        },
        // {
        //   kind: 'imageUpload',
        //   icon: 'i-lucide-image',
        //   tooltip: { text: 'Image' },
        // },
      ],
    ] satisfies EditorToolbarItem[][],
)

const { copy, copied, isSupported } = useClipboard()
const toast = useToast()
whenever(copied, () => {
  toast.add({
    title: 'Copied to clipboard',
  })
})

const selectedNode = shallowRef<{ node: JSONContent; pos: number }>()

// TODO: There is some typescript weirdness here. The UEditor scoped slot editor is slightly different from the @tiptap/vue-3 Editor type
const handleItems = (editor: Editor | any): DropdownMenuItem[][] => {
  if (!selectedNode.value?.node?.type) {
    return []
  }

  return mapEditorItems(editor as any, [
    [
      {
        type: 'label',
        label: upperFirst(selectedNode.value.node.type),
      },
      {
        label: 'Turn into',
        icon: 'i-lucide-repeat-2',
        children: [
          { kind: 'paragraph', label: 'Paragraph', icon: 'i-lucide-type' },
          {
            kind: 'heading',
            level: 1,
            label: 'Heading 1',
            icon: 'i-lucide-heading-1',
          },
          {
            kind: 'heading',
            level: 2,
            label: 'Heading 2',
            icon: 'i-lucide-heading-2',
          },
          {
            kind: 'heading',
            level: 3,
            label: 'Heading 3',
            icon: 'i-lucide-heading-3',
          },
          {
            kind: 'heading',
            level: 4,
            label: 'Heading 4',
            icon: 'i-lucide-heading-4',
          },
          { kind: 'bulletList', label: 'Bullet List', icon: 'i-lucide-list' },
          {
            kind: 'orderedList',
            label: 'Ordered List',
            icon: 'i-lucide-list-ordered',
          },
          {
            kind: 'blockquote',
            label: 'Blockquote',
            icon: 'i-lucide-text-quote',
          },
          {
            kind: 'codeBlock',
            label: 'Code Block',
            icon: 'i-lucide-square-code',
          },
        ],
      },
      {
        kind: 'clearFormatting',
        pos: selectedNode.value?.pos,
        label: 'Reset formatting',
        icon: 'i-lucide-rotate-ccw',
      },
    ],
    [
      {
        kind: 'duplicate',
        pos: selectedNode.value?.pos,
        label: 'Duplicate',
        icon: 'i-lucide-copy',
      },
      ...(isSupported
        ? [
            {
              label: 'Copy to clipboard',
              icon: 'i-lucide-clipboard',
              onSelect: async () => {
                if (!selectedNode.value) return

                const pos = selectedNode.value.pos
                const node = editor.state.doc.nodeAt(pos)
                if (node) {
                  await copy(node.textContent)
                }
              },
            },
          ]
        : []),
    ],
    [
      {
        kind: 'moveUp',
        pos: selectedNode.value?.pos,
        label: 'Move up',
        icon: 'i-lucide-arrow-up',
      },
      {
        kind: 'moveDown',
        pos: selectedNode.value?.pos,
        label: 'Move down',
        icon: 'i-lucide-arrow-down',
      },
    ],
    [
      {
        kind: 'delete',
        pos: selectedNode.value?.pos,
        label: 'Delete',
        icon: 'i-lucide-trash',
      },
    ],
  ]) as DropdownMenuItem[][]
}

const suggestionItems = [
  [
    {
      type: 'label',
      label: 'Style',
    },
    {
      kind: 'paragraph',
      label: 'Paragraph',
      icon: 'i-lucide-type',
    },
    {
      kind: 'heading',
      level: 1,
      label: 'Heading 1',
      icon: 'i-lucide-heading-1',
    },
    {
      kind: 'heading',
      level: 2,
      label: 'Heading 2',
      icon: 'i-lucide-heading-2',
    },
    {
      kind: 'heading',
      level: 3,
      label: 'Heading 3',
      icon: 'i-lucide-heading-3',
    },
    {
      kind: 'bulletList',
      label: 'Bullet List',
      icon: 'i-lucide-list',
    },
    {
      kind: 'orderedList',
      label: 'Numbered List',
      icon: 'i-lucide-list-ordered',
    },
    {
      kind: 'blockquote',
      label: 'Blockquote',
      icon: 'i-lucide-text-quote',
    },
    {
      kind: 'codeBlock',
      label: 'Code Block',
      icon: 'i-lucide-square-code',
    },
  ],
  [
    {
      type: 'label',
      label: 'Insert',
    },
    {
      kind: 'horizontalRule',
      label: 'Horizontal Rule',
      icon: 'i-lucide-separator-horizontal',
    },
  ],
] satisfies EditorSuggestionMenuItem[][]

/* An unedited doc's JSON is only a re-serialization of the loaded Markdown. */
const sendJsonUpdate = useDebounceFn((json: JSONContent) => {
  if (!loaded || !hasDocEdits.value || isEqual(json, jsonValue)) return

  emit('json', json)
}, 200)

/* The last Markdown this editor emitted, to tell our own echoes from a parent load. */
let lastEmitted: string | undefined

const sendMarkdownUpdate = useDebounceFn((markdown: string) => {
  lastEmitted = markdown
  emit('markdown', markdown)
}, 200)

// Transactions fire for more than user input: mount (the placeholder plugin
// dispatches one), selection changes, and every controlled content push — the
// `json` prop and the `loaded` hydration both end in a setContent. Reporting
// those back would hand the parent a re-serialized copy of text nobody edited,
// and the markdown serializer escapes `<`, `>` and `&`, so that copy is not
// byte-identical to what was loaded.
const hasDocEdits = ref(false)

/* A value we didn't emit is a fresh load: its edits start over, so its mirror isn't sent. */
watch(externalValue, (next) => {
  if (lastEmitted === undefined || (next ?? '') !== lastEmitted)
    hasDocEdits.value = false
})

const decodeEntities = (markdown: string) =>
  markdown.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')

// Compared, never stored — decoding is only how we recognise "this is still the
// value the parent gave us", without treating `<b>` in the text as markup.
const matchesExternalValue = (markdown: string) =>
  decodeEntities(markdown) === (externalValue.value ?? '')

const onCreate = ({ editor }: any) => {
  editorInstance.value = editor as Editor
  const json = editor.getJSON()
  sendJsonUpdate(json)
}

const onTransaction = ({ editor, transaction }: any) => {
  sendJsonUpdate(editor.getJSON())

  if (!transaction.docChanged) return

  // While still clean, a doc change only counts as an edit once the document
  // actually differs from the parent's value; once dirty, skip the comparison so
  // non-live editors serialize nothing extra per keystroke.
  if (!hasDocEdits.value) {
    const markdown = editor.getMarkdown()
    if (matchesExternalValue(markdown)) return

    hasDocEdits.value = true
    if (live) {
      sendMarkdownUpdate(markdown)
    }
    return
  }

  if (live) {
    sendMarkdownUpdate(editor.getMarkdown())
  }
}

const onBlur = ({ editor }: any) => {
  if (!hasDocEdits.value) return

  const markdown: string = editor.getMarkdown()
  lastEmitted = markdown
  emit('markdown', markdown)
}

/* A controlled value that changes underneath the editor — a peer's edit, an
   undo applied by the server — is taken in, except while this editor has
   focus: mid-typing, the echo of our own debounced update would overwrite
   newer keystrokes. */
const root = useTemplateRef('root')
watch(externalValue, (next) => {
  const editor = root.value?.editor as Editor | undefined
  if (!editor || editor.isFocused) return
  if (matchesExternalValue(editor.getMarkdown())) return
  value.value = next
})

defineExpose({
  focus: () =>
    (root.value?.editor as Editor | undefined)?.commands.focus('end'),
})
</script>

<template>
  <UEditor
    ref="root"
    v-slot="{ editor, handlers }"
    v-model="value"
    class="w-full"
    :editable="!readonly"
    :placeholder="placeholder"
    :ui="{
      content: readonly
        ? 'px-0 py-2'
        : compact
          ? 'pl-8 pr-2 py-2'
          : 'pl-8 pr-4 py-4',
    }"
    content-type="markdown"
    @blur="onBlur"
    @create="onCreate"
    @transaction="onTransaction"
  >
    <UEditorToolbar
      v-if="!readonly"
      class="border-muted bg-muted sticky inset-x-0 top-0 z-50 w-full overflow-x-auto border-b py-2"
      :class="compact ? 'px-2' : 'px-8 sm:px-16'"
      :editor="editor"
      :items="fixedToolbarItems"
    >
      <template #link>
        <EditorLinkPopover :editor="editor" auto-open />
      </template>
    </UEditorToolbar>

    <!-- Above the sticky toolbar: a selection on the first line floats the
       bubble over the editor's top edge, where the toolbar covered it. -->
    <UEditorToolbar
      :editor="editor"
      :items="bubbleToolbarItems"
      :should-show="
        ({ editor, view, state }) => {
          if (editor.isActive('imageUpload') || editor.isActive('image')) {
            return false
          }
          const { selection } = state
          return view.hasFocus() && !selection.empty
        }
      "
      :ui="{ root: 'z-[60]' }"
      layout="bubble"
    >
      <template #link>
        <EditorLinkPopover :editor="editor" />
      </template>
    </UEditorToolbar>

    <UEditorDragHandle
      v-slot="{ ui, onClick }"
      :editor="editor"
      @node-change="selectedNode = $event"
    >
      <UButton
        :class="ui.handle()"
        color="neutral"
        icon="i-lucide-plus"
        size="sm"
        variant="ghost"
        @click="
          (e) => {
            e.stopPropagation()

            const selected = onClick()
            handlers.suggestion?.execute(editor, { pos: selected?.pos }).run()
          }
        "
      />

      <UDropdownMenu
        v-slot="{ open }"
        :content="{ side: 'left' }"
        :items="handleItems(editor)"
        :modal="false"
        :ui="{ content: 'w-48', label: 'text-xs' }"
        @update:open="editor.chain().setMeta('lockDragHandle', $event).run()"
      >
        <UButton
          :class="ui.handle()"
          :active="open"
          active-variant="soft"
          color="neutral"
          icon="i-lucide-grip-vertical"
          size="sm"
          variant="ghost"
        />
      </UDropdownMenu>
    </UEditorDragHandle>

    <UEditorSuggestionMenu :editor="editor" :items="suggestionItems" />
  </UEditor>
</template>

<style>
html.dark .tiptap .shiki,
html.dark .tiptap .shiki span {
  color: var(--shiki-dark) !important;
  background-color: var(--ui-bg-muted) !important;
}
</style>
